import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";

import {
  QUALIFYING_SCORE_GENDER_OPTIONS,
  QUALIFYING_SCORE_HEIGHT_OPTIONS,
  QUALIFYING_SCORE_SORT_TOKEN_LABELS,
} from "../../constants/data";
import { currentSeasonRange, todayIso } from "../../utils/qualifyingScoreUtils";
import QualifyingScoreSortPopover from "../QualifyingScoreSortPopover";
import MatchFilter from "./MatchFilter";

const useStyles = makeStyles({
  wrapper: {
    backgroundColor: "#fff",
    border: "1px solid #e0e6ef",
    borderRadius: "8px",
    padding: "12px 16px",
    marginBottom: "12px",
  },
  topRow: {
    display: "grid",
    gridTemplateColumns: "minmax(220px, 1fr) minmax(220px, 1fr)",
    gap: "12px",
    alignItems: "center",
  },
  filtersRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: "12px",
    marginTop: "12px",
  },
  chips: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flexWrap: "wrap",
  },
  sortRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginTop: "12px",
    flexWrap: "wrap",
  },
  rightActions: {
    display: "flex",
    gap: "8px",
    marginLeft: "auto",
  },
  asOfRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },
});

const QualifyingScoreFilters = ({
  governingBodies,
  governingBodyId,
  governingBodiesLoading,
  filters,
  sortTokens,
  isAscending,
  divisionOptions,
  regionOptions,
  onGoverningBodyChange,
  onFiltersChange,
  onSortChange,
  onReset,
  onCreate,
  canCreate,
  onExportCsv,
  onExportXlsx,
  canExport,
}) => {
  const classes = useStyles();
  const [sortAnchor, setSortAnchor] = useState(null);

  const gbOptions = useMemo(() => {
    const translationName = (gb) =>
      gb?.translations?.find?.((t) => t?.locale === "en-US")?.name ||
      gb?.translations?.[0]?.name ||
      "";

    return (Array.isArray(governingBodies) ? governingBodies : [])
      .map((gb) => ({
        id: gb.id,
        label: gb.name || translationName(gb) || gb.shortName || gb.abbreviation || `GB ${gb.id}`,
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
      );
  }, [governingBodies]);

  const selectedGbOption = gbOptions.find((o) => o.id === governingBodyId) || null;

  const handleAsOfQuickToday = () => {
    onFiltersChange({ asOfDate: todayIso() });
  };

  const handleAsOfQuickSeason = () => {
    const { from } = currentSeasonRange();
    onFiltersChange({ asOfDate: from });
  };

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.topRow}>
        <Autocomplete
          options={gbOptions}
          value={selectedGbOption}
          loading={governingBodiesLoading}
          getOptionLabel={(option) => option?.label || ""}
          getOptionSelected={(option, value) => option?.id === value?.id}
          onChange={(_, next) => onGoverningBodyChange(next?.id ?? null)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Governing body *"
              variant="outlined"
              size="small"
              placeholder="Select…"
            />
          )}
        />

        <Box className={classes.asOfRow}>
          <TextField
            label="As-of date"
            type="date"
            variant="outlined"
            size="small"
            value={filters?.asOfDate || ""}
            onChange={(e) => onFiltersChange({ asOfDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            style={{ flexGrow: 1 }}
          />
          <Chip label="Today" onClick={handleAsOfQuickToday} clickable size="small" />
          <Chip
            label="This season"
            onClick={handleAsOfQuickSeason}
            clickable
            size="small"
          />
        </Box>
      </Box>

      <Box className={classes.filtersRow}>
        <FormControl variant="outlined" size="small">
          <InputLabel>Gender</InputLabel>
          <Select
            value={filters?.gender || ""}
            onChange={(e) => onFiltersChange({ gender: e.target.value || null })}
            label="Gender"
          >
            <MenuItem value="">
              <em>Any</em>
            </MenuItem>
            {QUALIFYING_SCORE_GENDER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small">
          <InputLabel>Height</InputLabel>
          <Select
            value={filters?.eventHeight ?? ""}
            onChange={(e) =>
              onFiltersChange({
                eventHeight: e.target.value === "" ? null : Number(e.target.value),
              })
            }
            label="Height"
          >
            <MenuItem value="">
              <em>Any</em>
            </MenuItem>
            {QUALIFYING_SCORE_HEIGHT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Age group"
          variant="outlined"
          size="small"
          value={filters?.ageGroup || ""}
          onChange={(e) => onFiltersChange({ ageGroup: e.target.value || null })}
        />

        <MatchFilter
          label="Division"
          options={divisionOptions}
          value={filters?.divisionId || ""}
          match={filters?.divisionMatch}
          onChange={({ value, match }) =>
            onFiltersChange({ divisionId: value, divisionMatch: match })
          }
        />

        <MatchFilter
          label="Region"
          options={regionOptions}
          value={filters?.regionId || ""}
          match={filters?.regionMatch}
          onChange={({ value, match }) =>
            onFiltersChange({ regionId: value, regionMatch: match })
          }
        />

        <Button onClick={onReset} variant="text" size="small">
          Reset filters
        </Button>
      </Box>

      <Box className={classes.sortRow}>
        <Typography variant="body2" style={{ color: "#475467" }}>
          Sort:
        </Typography>
        <Box className={classes.chips}>
          {(sortTokens || []).map((token) => (
            <Chip
              key={token}
              label={`${QUALIFYING_SCORE_SORT_TOKEN_LABELS[token] || token} ${
                isAscending ? "↑" : "↓"
              }`}
              size="small"
              variant="outlined"
            />
          ))}
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={(e) => setSortAnchor(e.currentTarget)}
        >
          Edit sort
        </Button>

        <Box className={classes.rightActions}>
          <Button
            variant="outlined"
            size="small"
            onClick={onExportCsv}
            disabled={!canExport}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onExportXlsx}
            disabled={!canExport}
          >
            Export XLSX
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={onCreate}
            disabled={!canCreate}
          >
            + Create
          </Button>
        </Box>
      </Box>

      <QualifyingScoreSortPopover
        open={Boolean(sortAnchor)}
        anchorEl={sortAnchor}
        onClose={() => setSortAnchor(null)}
        sortTokens={sortTokens || []}
        isAscending={Boolean(isAscending)}
        onApply={(next) => onSortChange(next)}
      />
    </Box>
  );
};

QualifyingScoreFilters.propTypes = {
  governingBodies: PropTypes.array,
  governingBodyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  governingBodiesLoading: PropTypes.bool,
  filters: PropTypes.object,
  sortTokens: PropTypes.arrayOf(PropTypes.string),
  isAscending: PropTypes.bool,
  divisionOptions: PropTypes.arrayOf(PropTypes.string),
  regionOptions: PropTypes.arrayOf(PropTypes.string),
  onGoverningBodyChange: PropTypes.func.isRequired,
  onFiltersChange: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  canCreate: PropTypes.bool,
  onExportCsv: PropTypes.func,
  onExportXlsx: PropTypes.func,
  canExport: PropTypes.bool,
};

export default QualifyingScoreFilters;
